/**
 * useTablePreferences — Sync admin-table column preferences (visibility +
 * order) to the user's DB profile so the choices follow them across
 * devices.
 *
 * Falls back to localStorage if the API isn't reachable (offline /
 * brief network blips) so a save is never lost.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../services/api';

export default function useTablePreferences(tableKey, defaults) {
  const [visibleColumns, _setVisibleColumns] = useState(defaults);
  const [columnOrder, _setColumnOrder] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // Debounce saves so rapid toggling doesn't spam the API.
  const saveTimerRef = useRef(null);
  const lastSavedRef = useRef(null);

  // Initial load: try DB, fall back to localStorage, finally defaults.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let nextCols = defaults;
      let nextOrder = null;
      try {
        const res = await api.get(`/auth/me/table-preferences/${tableKey}`);
        const cols = res.data?.columns;
        const order = res.data?.column_order;
        if (cols && Object.keys(cols).length) {
          // Merge with defaults to pick up any new columns we've added since.
          nextCols = { ...defaults, ...cols };
        }
        if (Array.isArray(order) && order.length) {
          nextOrder = order;
        }
      } catch {
        // network blip — fall through to localStorage
        const lsRaw = localStorage.getItem(`table-prefs:${tableKey}`);
        if (lsRaw) {
          try {
            const parsed = JSON.parse(lsRaw);
            if (parsed.columns) nextCols = { ...defaults, ...parsed.columns };
            if (Array.isArray(parsed.column_order)) nextOrder = parsed.column_order;
          } catch { /* ignore corrupted ls */ }
        }
      }
      if (!cancelled) {
        _setVisibleColumns(nextCols);
        _setColumnOrder(nextOrder);
        lastSavedRef.current = JSON.stringify({ columns: nextCols, column_order: nextOrder });
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey]);

  // Debounced save whenever columns or order change.
  useEffect(() => {
    if (!loaded) return;
    const payload = { columns: visibleColumns, column_order: columnOrder };
    const serialised = JSON.stringify(payload);
    if (serialised === lastSavedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await api.put(`/auth/me/table-preferences/${tableKey}`, payload);
        lastSavedRef.current = serialised;
      } catch {
        // mirror to localStorage so the user's choice isn't lost
        try { localStorage.setItem(`table-prefs:${tableKey}`, serialised); } catch { /* quota / private mode */ }
      }
    }, 600);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [visibleColumns, columnOrder, loaded, tableKey]);

  const setVisibleColumns = useCallback((updater) => {
    _setVisibleColumns(prev => typeof updater === 'function' ? updater(prev) : updater);
  }, []);
  const setColumnOrder = useCallback((updater) => {
    _setColumnOrder(prev => typeof updater === 'function' ? updater(prev) : updater);
  }, []);

  const resetPreferences = useCallback(async () => {
    try { await api.delete(`/auth/me/table-preferences/${tableKey}`); } catch { /* ignore */ }
    try { localStorage.removeItem(`table-prefs:${tableKey}`); } catch { /* ignore */ }
    _setVisibleColumns(defaults);
    _setColumnOrder(null);
    lastSavedRef.current = JSON.stringify({ columns: defaults, column_order: null });
  }, [tableKey, defaults]);

  return {
    visibleColumns,
    setVisibleColumns,
    columnOrder,
    setColumnOrder,
    loaded,
    resetPreferences,
  };
}
